import { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { Label } from '@/types/general';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTranslations from '@/hooks/useTranslations';

interface CheckboxInputProps {
  field: string;
  label: Label;
  required?: boolean;
  description?: Label;
  color?: string;
  disabled?: boolean;
  onClick?: (e: ChangeEvent<HTMLInputElement, Element>) => void;
}

export default function CheckboxInput({
  field,
  label,
  required = false,
  description = '',
  color,
  disabled,
  onClick,
}: CheckboxInputProps) {
  const { getLocale } = useTranslations();
  const { control } = useFormContext();

  return (
    <Stack direction='column' spacing={0}>
      <Controller
        name={field as never}
        control={control}
        render={({ field: controllerField }) => (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => {
                  controllerField.onChange(e);
                  if (onClick) onClick(e);
                }}
                checked={!!controllerField?.value}
              />
            }
            label={getLocale(label)}
            required={required}
            sx={{
              color: color ?? 'primary.main',
            }}
            disabled={disabled}
          />
        )}
      />

      {description && (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {getLocale(description)}
        </Typography>
      )}
    </Stack>
  );
}
